/**
 * Paste URL Tab - Web Content Extraction for Step 3
 * User pastes a URL and Emma extracts content for knowledge base
 * 
 * UX Pattern: URL → Content extraction → Preview → Save
 * 
 * Enhanced (Feb 2026):
 * - SSRF-protected extraction via url_extraction_service
 * - Quality scoring display
 * - Entity attribution (client/partner/solution)
 * - Domain crawling mode: Discover and extract multiple pages from a website
 */

import React, { useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle, Globe, Copy, Eye, EyeOff, Gauge, FileText, Target, Link2, Layers, ArrowRight, ExternalLink } from 'lucide-react';
import { ClassificationPreview } from '../../knowledge/ClassificationPreview';
import { ContentClassificationResult } from '../../../types/content-classification';
import { apiRequest } from '../../../lib/api';

interface PasteURLTabProps {
  campaignId: string;
  organizationId: string;
  onContentAdded: (documentId: string, title: string, preview: string) => void;
  // Entity attribution - which entity this content is for
  entityId?: string;
  entityType?: 'client' | 'partner' | 'solution' | 'vendor';
  entityName?: string;
}

interface CrawlResult {
  domain: string;
  pagesDiscovered: number;
  pagesExtracted: number;
  pagesAddedToRag: number;
  documents: Array<{
    id: string;
    title: string;
    url: string;
    quality_score: number;
    word_count: number;
    asset_type?: string;
  }>;
  discoveredPages: Array<{
    url: string;
    title: string;
    page_type: string;
    priority: number;
  }>;
}

export const PasteURLTab: React.FC<PasteURLTabProps> = ({
  campaignId,
  organizationId,
  onContentAdded,
  entityId,
  entityType,
  entityName
}) => {
  // Mode: 'single' for one URL, 'crawl' for entire domain
  const [mode, setMode] = useState<'single' | 'crawl'>('single');
  const [url, setUrl] = useState('');
  const [contentType, setContentType] = useState('web_content');
  const [isLoading, setIsLoading] = useState(false);
  const [extractedContent, setExtractedContent] = useState<{
    title: string;
    preview: string;
    documentId: string;
    qualityScore: number;
    wordCount: number;
    extractionStrategy: string;
    suggestedAssetType?: string;
  } | null>(null);
  // Domain crawl results
  const [crawlResult, setCrawlResult] = useState<CrawlResult | null>(null);
  const [maxPages, setMaxPages] = useState(10);
  const [minQuality, setMinQuality] = useState(40);
  const [showPreview, setShowPreview] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Classification state
  const [classification, setClassification] = useState<ContentClassificationResult | null>(null);
  const [classifying, setClassifying] = useState(false);
  const [classificationError, setClassificationError] = useState<string | null>(null);
  const [classificationExpanded, setClassificationExpanded] = useState(false);

  const contentTypes = [
    { id: 'case_study', label: '📊 Case Study Page' },
    { id: 'product_page', label: '🛍️ Product Page' },
    { id: 'blog_post', label: '📝 Blog Post' },
    { id: 'whitepaper', label: '📄 Whitepaper/Research' },
    { id: 'company_profile', label: '🏢 Company Profile' },
    { id: 'web_content', label: '🌐 Other Web Content' }
  ];

  // Quality score color based on value
  const getQualityColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 50) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  // Classify document using CS11 9-facet classification
  const classifyDocument = async (documentId: string) => {
    setClassifying(true);
    setClassificationError(null);
    
    try {
      const response = await apiRequest(`/api/v1/classifications/${documentId}/classify`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to classify document');
      }
      
      const result = await response.json();
      setClassification(result);
    } catch (error) {
      console.error('Classification failed:', error);
      setClassificationError(error instanceof Error ? error.message : 'Failed to classify document');
    } finally {
      setClassifying(false);
    }
  };

  // Retry classification after error
  const retryClassification = () => {
    if (extractedContent?.documentId) {
      classifyDocument(extractedContent.documentId);
    }
  };

  const handleExtractContent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url.trim()) {
      setErrorMessage('Please enter a URL');
      return;
    }

    // Validate URL
    try {
      new URL(url);
    } catch {
      setErrorMessage('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    setIsLoading(true);
    setStatus('loading');
    setErrorMessage('');

    try {
      const requestBody: Record<string, unknown> = {
        organization_id: organizationId,
        url: url,
        content_type: contentType,
        title: undefined // Let backend extract from HTML
      };
      
      // Only include campaign_id if it's a valid non-empty string
      if (campaignId && campaignId.trim()) {
        requestBody.campaign_id = campaignId;
      }
      
      // Add entity attribution if provided
      if (entityId && entityType) {
        requestBody.entity_id = entityId;
        requestBody.entity_type = entityType;
      }

      const response = await apiRequest(`/api/v1/workflow/step3/paste-url`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Handle Pydantic validation errors (detail is array) and string errors
        let errorDetail = errorData.detail;
        if (Array.isArray(errorDetail)) {
          errorDetail = errorDetail.map((e: { msg?: string }) => e.msg || String(e)).join('; ');
        } else if (typeof errorDetail === 'object' && errorDetail !== null) {
          errorDetail = errorDetail.msg || JSON.stringify(errorDetail);
        }
        throw new Error(errorDetail || `Failed to extract content: ${response.statusText}`);
      }

      const result = await response.json();

      setExtractedContent({
        title: result.title,
        preview: result.preview,
        documentId: result.document_id,
        qualityScore: result.quality_score || 0,
        wordCount: result.word_count || 0,
        extractionStrategy: result.extraction_strategy || 'unknown',
        suggestedAssetType: result.suggested_asset_type
      });

      setStatus('success');
      onContentAdded(result.document_id, result.title, result.preview);
      
      // Trigger classification after successful extraction
      if (result.document_id) {
        classifyDocument(result.document_id);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to extract content';
      setErrorMessage(errorMsg);
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle domain crawl submission
  const handleCrawlDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url.trim()) {
      setErrorMessage('Please enter a domain URL');
      return;
    }

    // Validate URL (should be a domain, not a deep page)
    let domainUrl = url;
    try {
      const parsed = new URL(url);
      // Simplify to just the origin (domain)
      domainUrl = parsed.origin;
    } catch {
      setErrorMessage('Please enter a valid URL (e.g., https://company.com)');
      return;
    }

    setIsLoading(true);
    setStatus('loading');
    setErrorMessage('');

    try {
      const requestBody: Record<string, unknown> = {
        organization_id: organizationId,
        domain_url: domainUrl,
        max_pages: maxPages,
        min_quality: minQuality
      };
      
      // Only include campaign_id if it's a valid non-empty string
      if (campaignId && campaignId.trim()) {
        requestBody.campaign_id = campaignId;
      }
      
      if (entityId && entityType) {
        requestBody.entity_id = entityId;
        requestBody.entity_type = entityType;
      }

      const response = await apiRequest(`/api/v1/workflow/step3/crawl-domain`, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Handle Pydantic validation errors (detail is array) and string errors
        let errorDetail = errorData.detail;
        if (Array.isArray(errorDetail)) {
          errorDetail = errorDetail.map((e: { msg?: string }) => e.msg || String(e)).join('; ');
        } else if (typeof errorDetail === 'object' && errorDetail !== null) {
          errorDetail = errorDetail.msg || JSON.stringify(errorDetail);
        }
        throw new Error(errorDetail || `Failed to crawl domain: ${response.statusText}`);
      }

      const result = await response.json();

      setCrawlResult({
        domain: result.domain,
        pagesDiscovered: result.pages_discovered,
        pagesExtracted: result.pages_extracted,
        pagesAddedToRag: result.pages_added_to_rag,
        documents: result.documents,
        discoveredPages: result.discovered_pages
      });

      setStatus('success');
      
      // Notify parent of all added documents
      result.documents.forEach((doc: { id: string; title: string; url: string }) => {
        onContentAdded(doc.id, doc.title, doc.url);
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to crawl domain';
      setErrorMessage(errorMsg);
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (extractedContent?.preview) {
      navigator.clipboard.writeText(extractedContent.preview);
    }
  };

  return (
    <div className="space-y-4">
      {/* Entity Attribution Banner (if provided) */}
      {entityId && entityType && entityName && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200/80 bg-emerald-50/90 p-3 text-sm text-emerald-900">
          <Target className="w-4 h-4" />
          <span>Content will be attributed to: <strong>{entityName}</strong> ({entityType})</span>
        </div>
      )}

      {/* Mode Toggle: Single URL vs Crawl Domain */}
      {!extractedContent && !crawlResult && (
        <div className="flex gap-2 rounded-xl border border-emerald-100 bg-emerald-50/40 p-1">
          <button
            type="button"
            onClick={() => setMode('single')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
              mode === 'single'
                ? 'bg-white text-emerald-800 shadow-sm ring-1 ring-emerald-200/80'
                : 'text-emerald-900/60 hover:bg-white/60 hover:text-emerald-900'
            }`}
          >
            <Link2 className="w-4 h-4" />
            Single URL
          </button>
          <button
            type="button"
            onClick={() => setMode('crawl')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
              mode === 'crawl'
                ? 'bg-white text-emerald-800 shadow-sm ring-1 ring-emerald-200/80'
                : 'text-emerald-900/60 hover:bg-white/60 hover:text-emerald-900'
            }`}
          >
            <Layers className="w-4 h-4" />
            Crawl Domain
          </button>
        </div>
      )}

      {/* SINGLE URL MODE: URL Input Form */}
      {mode === 'single' && !extractedContent && !crawlResult && (
        <form onSubmit={handleExtractContent} className="space-y-4">
          {/* URL Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Paste a URL</label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://example.com/case-study"
                  className="w-full rounded-xl border border-emerald-200/80 py-2 pl-10 pr-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              </div>
            </div>
            {errorMessage && (
              <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4" />
                {errorMessage}
              </div>
            )}
          </div>

          {/* Content Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">What type of content?</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {contentTypes.map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setContentType(type.id)}
                  className={`p-2 rounded-lg border-2 text-sm text-left transition-colors ${
                    contentType === type.id
                      ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200/60'
                      : 'border-gray-200 bg-white hover:border-emerald-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !url.trim()}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-emerald-600 px-4 py-2.5 font-semibold text-white shadow-md transition-all hover:border-emerald-700 hover:bg-emerald-700 hover:shadow-lg active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Extracting content...
              </>
            ) : (
              <>
                <Globe className="w-4 h-4" />
                Extract Content
              </>
            )}
          </button>
        </form>
      )}

      {/* CRAWL DOMAIN MODE: Crawl entire website */}
      {mode === 'crawl' && !extractedContent && !crawlResult && (
        <form onSubmit={handleCrawlDomain} className="space-y-4">
          {/* Domain URL Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Enter a domain URL</label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Layers className="absolute left-3 top-1/2 w-4 -translate-y-1/2 transform text-emerald-500" />
                <input
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://company.com"
                  className="w-full rounded-xl border border-emerald-200/80 py-2 pl-10 pr-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              We'll discover and extract content from key pages like About, Products, Case Studies
            </p>
            {errorMessage && (
              <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4" />
                {errorMessage}
              </div>
            )}
          </div>

          {/* Crawl Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Max Pages</label>
              <select
                value={maxPages}
                onChange={e => setMaxPages(parseInt(e.target.value))}
                className="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm"
              >
                <option value="5">5 pages</option>
                <option value="10">10 pages</option>
                <option value="20">20 pages</option>
                <option value="30">30 pages</option>
                <option value="50">50 pages (max)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Min Quality</label>
              <select
                value={minQuality}
                onChange={e => setMinQuality(parseInt(e.target.value))}
                className="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm"
              >
                <option value="20">20+ (include most)</option>
                <option value="40">40+ (balanced)</option>
                <option value="60">60+ (quality focus)</option>
                <option value="80">80+ (only high quality)</option>
              </select>
            </div>
          </div>

          {/* Crawl Button */}
          <button
            type="submit"
            disabled={isLoading || !url.trim()}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-teal-600 bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-2.5 font-semibold text-white shadow-md transition-all hover:from-emerald-700 hover:to-teal-700 hover:shadow-lg active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Discovering pages...
              </>
            ) : (
              <>
                <Layers className="w-4 h-4" />
                Crawl Domain
              </>
            )}
          </button>

          {/* Crawl Tips */}
          <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/80 p-3">
            <div className="text-sm text-emerald-950">
              <div className="mb-2 font-medium">🌐 Domain crawl will discover:</div>
              <ul className="space-y-1 text-xs text-emerald-900/85">
                <li>• About/Company pages → Company context</li>
                <li>• Product pages → Solution details</li>
                <li>• Case studies → Customer success stories</li>
                <li>• Partner pages → Ecosystem info</li>
                <li>• Blog posts → Industry insights</li>
              </ul>
            </div>
          </div>
        </form>
      )}

      {/* CRAWL RESULT: Show discovered pages */}
      {crawlResult && (
        <div className="space-y-4">
          <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/90 p-4">
            <div className="mb-2 flex items-center gap-2 font-medium text-emerald-950">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Domain crawl complete!
            </div>
            <div className="text-sm text-emerald-900/85">
              Discovered {crawlResult.pagesDiscovered} pages, added {crawlResult.pagesAddedToRag} to knowledge base
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-emerald-200/70 bg-white p-3 shadow-sm">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-700/80">Discovered</div>
              <div className="text-lg font-bold text-emerald-950">{crawlResult.pagesDiscovered}</div>
            </div>
            <div className="rounded-xl border border-emerald-200/70 bg-white p-3 shadow-sm">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-700/80">Extracted</div>
              <div className="text-lg font-bold text-emerald-950">{crawlResult.pagesExtracted}</div>
            </div>
            <div className="rounded-xl border border-teal-200/80 bg-gradient-to-br from-emerald-50 to-teal-50 p-3 shadow-sm">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-teal-800">Added to KB</div>
              <div className="text-lg font-bold text-emerald-950">{crawlResult.pagesAddedToRag}</div>
            </div>
          </div>

          {/* Pages Added */}
          {crawlResult.documents.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Pages Added to Knowledge Base</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {crawlResult.documents.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-2 border rounded-lg bg-white text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{doc.title}</div>
                      <a 
                        href={doc.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 hover:underline"
                      >
                        {new URL(doc.url).pathname}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityColor(doc.quality_score)}`}>
                        {doc.quality_score}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Crawl Again Button */}
          <button
            onClick={() => {
              setCrawlResult(null);
              setUrl('');
              setStatus('idle');
              setErrorMessage('');
            }}
            className="btn-emerald-outline h-11 w-full rounded-xl px-4 text-sm font-semibold"
          >
            + Crawl Another Domain
          </button>
        </div>
      )}

      {/* Success State - Content Preview with Quality Metrics */}
      {extractedContent && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-900 font-medium mb-2">
              <CheckCircle2 className="w-5 h-5" />
              Content extracted successfully
            </div>
            <div className="text-sm text-green-800">
              {extractedContent.title} has been added to your knowledge base.
            </div>
          </div>

          {/* Quality Metrics */}
          <div className="grid grid-cols-3 gap-3">
            <div className={`p-3 rounded-lg border ${getQualityColor(extractedContent.qualityScore)}`}>
              <div className="flex items-center gap-1 text-xs font-medium mb-1">
                <Gauge className="w-3 h-3" />
                Quality
              </div>
              <div className="text-lg font-bold">{extractedContent.qualityScore}/100</div>
            </div>
            <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
              <div className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1">
                <FileText className="w-3 h-3" />
                Words
              </div>
              <div className="text-lg font-bold text-gray-900">{extractedContent.wordCount.toLocaleString()}</div>
            </div>
            <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
              <div className="text-xs font-medium text-gray-600 mb-1">Type</div>
              <div className="text-sm font-medium text-gray-900">
                {extractedContent.suggestedAssetType?.replace(/_/g, ' ') || contentType.replace(/_/g, ' ')}
              </div>
            </div>
          </div>

          {/* Content Preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Preview</label>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-900"
              >
                {showPreview ? (
                  <>
                    <EyeOff className="w-4 h-4" />
                    Hide
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4" />
                    Show
                  </>
                )}
              </button>
            </div>

            {showPreview && (
              <div className="border rounded-lg bg-gray-50 p-4 max-h-64 overflow-y-auto">
                <p className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                  {extractedContent.preview}
                </p>
                <button
                  onClick={copyToClipboard}
                  className="mt-3 flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-900"
                >
                  <Copy className="w-4 h-4" />
                  Copy preview
                </button>
              </div>
            )}
          </div>

          {/* Content Classification (CS11 9-facet preview) */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              Content Classification
              {classification && !classifying && !classificationError && (
                <span className="flex items-center gap-1 text-xs font-normal text-green-600">
                  <CheckCircle2 className="w-3 h-3" />
                  Classified
                </span>
              )}
            </h3>
            
            {/* Classification Loading State */}
            {classifying && (
              <div className="flex items-center gap-2 rounded-xl border border-emerald-200/80 bg-emerald-50/80 p-4 text-sm text-emerald-900/80">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Classifying content using CS11 9-facet taxonomy...</span>
              </div>
            )}
            
            {/* Classification Error State */}
            {classificationError && !classifying && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-red-900 mb-2">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">Classification failed</span>
                </div>
                <p className="text-sm text-red-700 mb-3">{classificationError}</p>
                <button
                  onClick={retryClassification}
                  className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-800 text-sm rounded-md transition-colors"
                >
                  Retry Classification
                </button>
              </div>
            )}
            
            {/* Classification Success State - Expandable 9-Facet Preview */}
            {classification && !classifying && !classificationError && (
              <div className="space-y-3">
                {/* Confidence Badge */}
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-md">
                    {classification.confidence ? `${Math.round(classification.confidence * 100)}% confidence` : 'Classified'}
                  </span>
                </div>
                
                {/* Expandable Classification Preview */}
                <div>
                  <button
                    onClick={() => setClassificationExpanded(!classificationExpanded)}
                    className="flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-900"
                  >
                    {classificationExpanded ? '▼' : '▶'} 
                    {classificationExpanded ? 'Hide' : 'View'} All 9 Classification Facets
                  </button>
                  
                  <div
                    className={`overflow-hidden transition-all duration-300 ${classificationExpanded ? 'max-h-[2000px] mt-3' : 'max-h-0'}`}
                  >
                    <ClassificationPreview 
                      classification={classification}
                      readOnly={true}
                      showConfidence={true}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Next Steps */}
          <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/80 p-4">
            <div className="text-sm text-emerald-950">
              <div className="mb-2 font-medium">✓ Next Steps:</div>
              <ul className="space-y-1 text-emerald-900/85">
                <li>• Content is being vectorized and added to your knowledge base</li>
                {entityId && <li>• Linked to {entityName || entityType} for context-aware retrieval</li>}
                <li>• Emma will use this content for more specific, credible messaging</li>
              </ul>
            </div>
          </div>

          {/* Add Another URL Button */}
          <button
            onClick={() => {
              setExtractedContent(null);
              setCrawlResult(null);
              setUrl('');
              setStatus('idle');
              setErrorMessage('');
            }}
            className="btn-emerald-outline h-11 w-full rounded-xl px-4 text-sm font-semibold"
          >
            + Add Another URL
          </button>
        </div>
      )}

      {/* Helpful Tips (only shown when in input mode) */}
      {!extractedContent && !crawlResult && mode === 'single' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="text-sm text-amber-900">
            <div className="font-medium mb-2">💡 Tips for best results:</div>
            <ul className="space-y-1 text-amber-800 text-xs">
              <li>• Case studies: URLs with customer success stories, ROI results</li>
              <li>• Product pages: URLs showcasing your solutions, features, benefits</li>
              <li>• Company profiles: Vendor/partner "About Us" pages for context</li>
              <li>• Research: Industry reports, whitepapers, analyst reports</li>
              <li>• Content must be publicly accessible (no login required)</li>
              <li>• <strong>Need multiple pages?</strong> Use "Crawl Domain" mode instead</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
